import React, { useState } from 'react';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormattedMessageV2 } from '@/views/components/i18n';
import { Input } from '@/views/components/inputs';

// Enums
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Libraries
import { string } from 'yup';

// Types
import { Email } from '@/types/models/risk-profiles/omni-risk-profile-stats';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';

// Utils
import datadogUtils, { DATADOG_ACTIONS } from '@/utils/3rd-party/datadogUtils';
import notification from '@/utils/notification';
import { formatMessage } from '@/utils/i18n';

const MAX_SHARED_EMAIL = 1;

// Validation
const emailSchema = string().email().required();

interface SendToFieldProps {
  authorizeEmail: (email: string) => Promise<void>;
  sharedEmails: Email[];
  hasEnforcedContactEmail: boolean;
  initialEmail?: string;
  riskProfile: FormattedFullOmniRiskProfile;
}

export const SendToField: React.FC<SendToFieldProps> = ({ authorizeEmail, hasEnforcedContactEmail, initialEmail, riskProfile, sharedEmails }) => {
  const { emails } = riskProfile;
  const [sharedEmail, setSharedEmail] = useState<string>(initialEmail || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isValidEmail = emailSchema.isValidSync(sharedEmail);
  const isUserLimitReached = emails.length >= MAX_SHARED_EMAIL;

  const handleAuthorizeEmail = async (): Promise<void> => {
    // Validate email before proceeding
    if (!emailSchema.isValidSync(sharedEmail)) {
      return notification.error(formatMessage('RiskProfileModal.SendQuestionnaire.InvalidEmail'));
    }

    setIsLoading(true);

    await authorizeEmail(sharedEmail);
    if (!hasEnforcedContactEmail) {
      setSharedEmail('');
    }

    datadogUtils.addAction(DATADOG_ACTIONS.RISK_PROFILE.SEND_QUESTIONNAIRE.AUTHORIZE_EMAIL);

    setIsLoading(false);
  };

  return (
    <div className="send-container" data-testid="sendQuestionnaire-send-container">
      <div className="send-row">
        <div className="form-container">
          <Input
            className="input-v2 send-questionnaire-input"
            data-testid="sendQuestionnaire-input-sendTo"
            onValueChange={setSharedEmail}
            placeholder={formatMessage('RiskProfileModal.SendQuestionnaire.Placeholder')}
            type="email"
            value={sharedEmail}
            disabled={hasEnforcedContactEmail || isLoading || !!sharedEmails.length}
          />
        </div>
        <Button
          variant="primary"
          loading={isLoading}
          data-testid="sendQuestionnaire-primaryButton-send"
          disabled={!!sharedEmails.length || !isValidEmail}
          onClick={handleAuthorizeEmail}
        >
          {formatMessage('RiskProfileModal.SendQuestionnaire.AuthorizeEmail')}
        </Button>
      </div>
      {!!sharedEmails.length && isUserLimitReached && (
        <FormattedMessageV2 className="send-limit-reached" tagName={ALLOWED_HTML_TAGS.P} toLocalize="RiskProfileModal.SendQuestionnaire.MaxUserLimit" />
      )}
    </div>
  );
};
